import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";
import { StudyStream as StudyStreamSection } from "@/components/StudyStream";
import { Users, Clock, Globe, Zap, Video, MessageCircle, Play, ArrowRight, Infinity } from "lucide-react";

const features = [
  {
    icon: Video,
    title: "Live Study Rooms",
    description: "Join 24/7 virtual study spaces with webcam sharing. See others working and stay accountable.",
  },
  {
    icon: Users,
    title: "Global Community",
    description: "Connect with students worldwide. Study alongside peers from different time zones and cultures.",
  },
  {
    icon: MessageCircle,
    title: "Chat & Support",
    description: "Communicate with fellow studiers. Share tips, ask questions, and motivate each other.",
  },
  {
    icon: Clock,
    title: "Flexible Sessions",
    description: "No time limits. Study for as long as you need without interruptions or restrictions.",
  },
  {
    icon: Globe,
    title: "Multi-Language",
    description: "Available in English, Japanese, Vietnamese, Chinese, and Korean for global accessibility.",
  },
  {
    icon: Zap,
    title: "Instant Access",
    description: "Be productive in less than 10 seconds. No complex setup or waiting required.",
  },
];

const howItWorks = [
  {
    step: "1",
    title: "Choose a Room",
    description: "Browse available study rooms or create your own. Pick a vibe that matches your mood.",
  },
  {
    step: "2",
    title: "Join & Connect",
    description: "Enter the room with one click. Enable your webcam to join the community (optional).",
  },
  {
    step: "3",
    title: "Study Together",
    description: "Focus on your work while surrounded by others doing the same. Stay motivated and accountable.",
  },
];

export default function StudyStreamPage() {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="Study Stream - Free 24/7 Virtual Study Rooms"
        description="Join free unlimited study streams with no time limits. Connect with students worldwide in 24/7 virtual study rooms. No payment required, ever."
        keywords="study stream, free study room, virtual study room, study with me live, online study session"
        canonicalUrl="/study-stream/"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-background via-muted to-background" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/20 rounded-full blur-[120px] animate-glow-pulse" style={{ animationDelay: '1s' }} />
        
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 animate-fade-in">
              <Infinity className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold text-primary">NO TIME LIMIT • FREE FOR EVERYONE</span>
            </div>
            
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <span className="text-gradient">Study Stream</span>
            </h1>
            
            <p className="text-xl sm:text-2xl text-muted-foreground max-w-3xl mx-auto font-light animate-fade-in" style={{ animationDelay: '0.2s' }}>
              24/7 Virtual Study Rooms. Free for Everyone.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <Button 
                size="lg"
                className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg glow-primary transition-all hover:scale-105"
                asChild
              >
                <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                  Try Now - Be Productive in 10 Seconds <ArrowRight className="ml-2 w-5 h-5" />
                </a>
              </Button>
            </div>
            
            {/* Video Section */}
            <div className="relative mt-12 animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <div className="relative rounded-2xl overflow-hidden border-2 border-border/50 bg-card/30 backdrop-blur-sm shadow-2xl">
                <video 
                  autoPlay 
                  loop 
                  muted 
                  playsInline
                  className="w-full aspect-video object-cover"
                >
                  <source src="/videos/productivity-demo.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-transparent" />
                <div className="absolute bottom-6 left-6 flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/90 flex items-center justify-center glow-primary">
                    <Play className="w-5 h-5 text-primary-foreground ml-1" />
                  </div>
                  <span className="text-foreground font-medium">See how it works</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-6 text-foreground">
              <h2 className="font-display text-4xl sm:text-5xl font-bold mb-8">
                24/7 Virtual <span className="text-gradient">Study Room</span>
              </h2>
              
              <p className="text-muted-foreground text-lg leading-relaxed">
                CSW's Study Stream is a 24/7 virtual study room network where learners worldwide stay productive side by side. 
                It's a live online environment that transforms solitary studying into a collaborative journey. With Study Stream, 
                you can jump into a focused session at any time and be alone, but never lonely – surrounded by a community of 
                peers all working toward their goals.
              </p>
              
              <p className="text-muted-foreground text-lg leading-relaxed">
                Short on motivation? Struggling to stay on task? Don't pay to be productive. CSW's Study Stream is completely 
                free and has no limits on how long or how often you can join. Our competitors might restrict you or charge fees, 
                but we believe productivity should be accessible to all.
              </p>
              
              <Card className="p-8 bg-gradient-to-br from-primary/10 to-secondary/10 border border-primary/20">
                <p className="text-xl font-semibold text-foreground mb-3">Why Study Stream is Different</p>
                <p className="text-muted-foreground text-lg">
                  CSW's Study Stream is free to use, with unlimited session time and no cap on how often you can join. 
                  Our philosophy is simple: you shouldn't have to pay to be productive. Unlike some other platforms that 
                  restrict usage for free members or require costly subscriptions for full access, Study Stream's core 
                  features are completely free for everyone.
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 relative overflow-hidden">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-display text-4xl sm:text-5xl font-bold mb-4">
                Study <span className="text-gradient">Stream</span>
              </h2>
              <p className="text-xl text-muted-foreground">Powerful features to keep you focused and connected</p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <Card 
                  key={feature.title} 
                  className="p-8 bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105 animate-fade-in"
                  style={{ animationDelay: `${0.1 * index}s` }}
                >
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center mb-6">
                    <feature.icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-foreground">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-muted/50 to-background" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="font-display text-4xl sm:text-5xl font-bold mb-4">
                Free Study <span className="text-gradient">Stream</span>
              </h2>
              <p className="text-xl text-muted-foreground">Get started in three simple steps</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {howItWorks.map((item, index) => (
                <div key={item.step} className="text-center space-y-6 animate-fade-in" style={{ animationDelay: `${0.1 * index}s` }}>
                  <div className="mx-auto w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center glow-primary">
                    <span className="text-3xl font-bold text-primary-foreground">{item.step}</span>
                  </div>
                  
                  <h3 className="text-xl font-bold text-foreground">{item.title}</h3>
                  <p className="text-muted-foreground">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing/Free Section */}
      <StudyStreamSection />

      {/* Final CTA */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/10 rounded-full blur-[150px]" />
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Ready to Transform Your <span className="text-gradient">Study Sessions?</span>
            </h2>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Join thousands of students worldwide who are achieving more with Study Stream. 
              No credit card required. No time limits. Just pure productivity.
            </p>
            <Button 
              size="lg"
              className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-12 py-6 text-lg glow-primary transition-all hover:scale-105"
              asChild
            >
              <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                Start Studying for Free
              </a>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}